import React, { useEffect, useMemo } from "react";
import ModalComponent from "../../../components/modals";
import SimpleSelect from "../../../components/selects/simpleSelect";
import LaddaButton from "../../../components/buttons/laddaButton";
import { Grid } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
  impactData,
  probabilityData,
  vulnerabilityData,
  controlType,
  controlFrequency
} from "../../../constants/selectData";
import { getRiskScore, getRiskLevelFromScore } from "../../createRisk/riskLevelLogic";
import { callApiAsync, postUpdateRiskValuation } from "../../../services/apiService";
import { icons } from "../../../constants";
import { useSelector } from "react-redux";

const residualRiskLevel = (levelItem) => {
  if (levelItem == null || levelItem === undefined || levelItem === Infinity) {
    return { class: "default-level", textClass: "default-level-text", text: "—" };
  }
  const v = parseFloat(levelItem);
  if (v < 5) return { class: "low-level", textClass: "low-level-text", text: "Tolerable" };
  if (v <= 7) return { class: "medium-level", textClass: "medium-level-text", text: "Moderado" };
  if (v < 12) return { class: "high-level", textClass: "high-level-text", text: "Importante" };
  return { class: "extreme-level", textClass: "extreme-level-text", text: "Inaceptable" };
};

const controlEffectivenessLevel = (levelItem) => {
  const level = parseInt(levelItem, 10);
  if (level >= 9) return { class: "high-level", textClass: "high-level-text", text: "Alto" };
  if (level >= 5) return { class: "medium-level", textClass: "medium-level-text", text: "Medio" };
  if (level >= 1) return { class: "low-level", textClass: "low-level-text", text: "Bajo" };
  return { class: "default-level", textClass: "default-level-text", text: "—" };
};

const EditValuationModal = ({
  open,
  handleClose,
  data,
  getRisk,
  useVersion2,
  updateRiskupdateDate
}) => {
  const userData = useSelector((state) => state.userData.userData);
  const schema = yup.object().shape({
    impact: yup.string().required("Requerido"),
    probability: yup.string().required("Requerido"),
    typeControl: yup.string().required("Requerido"),
    controlFrequency: yup.string().required("Requerido"),
    ...(useVersion2 ? { vulnerability: yup.string().required("Requerido") } : {})
  });

  const { control, handleSubmit, setValue, watch, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      impact: "",
      probability: "",
      vulnerability: "",
      typeControl: "",
      controlFrequency: ""
    }
  });

  const watchImpact = watch("impact");
  const watchProbability = watch("probability");
  const watchVulnerability = watch("vulnerability");
  const watchTypeControl = watch("typeControl");
  const watchControlFrequency = watch("controlFrequency");

  const score = React.useMemo(() => {
    const i = parseInt(watchImpact, 10);
    const p = parseInt(watchProbability, 10);
    const v = useVersion2 ? parseInt(watchVulnerability, 10) : 1;
    if (!i || !p || (useVersion2 && !v)) return 0;
    return getRiskScore(i, v, p);
  }, [watchImpact, watchProbability, watchVulnerability, useVersion2]);

  const riskLevelInfo = getRiskLevelFromScore(score);
  const effectiveness = (parseInt(watchTypeControl, 10) || 0) * (parseInt(watchControlFrequency, 10) || 0);
  const residualValue = effectiveness > 0 ? score / effectiveness : 0;
  const residualInfo = residualRiskLevel(residualValue);
  const effectivenessInfo = controlEffectivenessLevel(effectiveness);

  useEffect(() => {
    if (!open || !data) return;
    setValue("impact", data.impact != null ? String(data.impact) : "");
    setValue("probability", data.probability != null ? String(data.probability) : "");
    setValue("vulnerability", data.vulnerability != null ? String(data.vulnerability) : "");
    setValue("typeControl", data.typeControl != null ? String(data.typeControl) : "");
    setValue("controlFrequency", data.frecuencyControl != null ? String(data.frecuencyControl) : "");
  }, [open, data, setValue]);

  const onSubmit = (formData) => {
    const payload = {
      id: data?.riskMonthId,
      impact: formData.impact,
      probability: formData.probability,
      typeControl: formData.typeControl,
      frecuencyControl: formData.controlFrequency,
      userId: userData.id
    };
    if (useVersion2) payload.vulnerability = formData.vulnerability;

    callApiAsync(
      () => postUpdateRiskValuation(payload),
      () => {
        if (updateRiskupdateDate) updateRiskupdateDate();
        getRisk();
        handleClose();
      }
    );
  };

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      title="Editar valoración del riesgo"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <p className="create-label ml-0">Impacto:</p>
            <SimpleSelect
              name="impact"
              control={control}
              options={impactData}
              error={errors.impact}
              hideLabel
              margin="0"
            />
          </Grid>
          {useVersion2 && (
            <Grid item xs={12} md={4}>
              <p className="create-label ml-0">Vulnerabilidad:</p>
              <SimpleSelect
                name="vulnerability"
                control={control}
                options={vulnerabilityData}
                error={errors.vulnerability}
                hideLabel
                margin="0"
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <p className="create-label ml-0">Probabilidad:</p>
            <SimpleSelect
              name="probability"
              control={control}
              options={probabilityData}
              error={errors.probability}
              hideLabel
              margin="0"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="IHContainer align-center pillcontainer">
              <p className="create-label ml-0">Nivel de Riesgo:</p>
              <div className={`level ${riskLevelInfo.class}`}>
                <p className={riskLevelInfo.textClass}>{riskLevelInfo.text}</p>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label ml-0">Tipo de Control:</p>
            <SimpleSelect
              name="typeControl"
              control={control}
              options={controlType}
              error={errors.typeControl}
              hideLabel
              margin="0"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label ml-0">Frecuencia del Control:</p>
            <SimpleSelect
              name="controlFrequency"
              control={control}
              options={controlFrequency}
              error={errors.controlFrequency}
              hideLabel
              margin="0"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="IHContainer align-center pillcontainer">
              <p className="create-label ml-0">Eficacia de Control:</p>
              <div className={`level ${effectivenessInfo.class}`}>
                <p className={effectivenessInfo.textClass}>{effectivenessInfo.text}</p>
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className="IHContainer align-center pillcontainer">
              <p className="create-label ml-0">Riesgo residual:</p>
              <div className={`level ${residualInfo.class}`}>
                <p className={residualInfo.textClass}>{residualInfo.text}</p>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} className="j-center mt-20">
            <LaddaButton type="submit" variant="contained">
              <div className="IHContainer align-center">
                <img className="btn-icon" src={icons.editWhite} alt="edit" style={{ marginRight: "8px" }} />
                <p className="button_text">Guardar</p>
              </div>
            </LaddaButton>
          </Grid>
        </Grid>
      </form>
    </ModalComponent>
  );
};

export default EditValuationModal;
